import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, ArrowLeft, Trees } from 'lucide-react';
import './ServiceDetail.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_2fdbb258-2fe3-4bcb-af8e-a32c1bf06202/artifacts/mg3au26l_WhatsApp%20Image%202026-03-21%20at%2015.53.49.jpeg';

const GartenpflegePage = () => {
  const [heroImage, setHeroImage] = useState(DEFAULT_IMAGE);

  useEffect(() => {
    document.title = 'Gartenpflege | Nessenius Garten & Landschaftsbau';
    window.scrollTo(0, 0);

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Professionelle Gartenpflege in Göttingen, Nörten-Hardenberg und Umgebung. Rasenmähen, Heckenschnitt, Beetpflege, Baumschnitt. Regelmäßig oder einmalig. Jetzt anrufen: 0176-55669090');

    // Fetch image from API
    const fetchImage = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/homepage-images`);
        if (response.ok) {
          const data = await response.json();
          if (data.gartenpflege?.image_url) {
            setHeroImage(data.gartenpflege.image_url);
          }
        }
      } catch (error) {
        console.error('Error fetching image:', error);
      }
    };
    fetchImage();
  }, []);

  return (
    <div className="service-detail-page">
      {/* Hero */}
      <section className="service-detail-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="service-detail-overlay"></div>
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={18} />
            Zurück zur Startseite
          </Link>
          <div className="service-detail-icon">
            <Trees size={40} />
          </div>
          <h1>Gartenpflege</h1>
          <p className="service-detail-subtitle">
            Von regelmäßigem Rasenmähen bis hin zum Hecken- und Baumschnitt sorgen wir für ein gepflegtes Gesamtbild.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="service-detail-content">
        <div className="container">
          <div className="service-detail-intro">
            <h2>Ihr Garten in guten Händen</h2>
            <p>
              Wir kümmern uns zuverlässig um die Pflege Ihres Gartens.
              Ob einmalig oder regelmäßig – wir sind für Sie da und sorgen dafür, 
              dass Ihr Garten das ganze Jahr über gepflegt aussieht.
            </p>
          </div>

          <div className="service-detail-grid">
            <div className="service-detail-card">
              <h3>Rasenpflege</h3>
              <ul>
                <li><CheckCircle size={18} /> Rasen mähen (einmalig oder regelmäßig)</li>
                <li><CheckCircle size={18} /> Rasenkanten schneiden</li>
                <li><CheckCircle size={18} /> Rasenneuanlage</li>
                <li><CheckCircle size={18} /> Rasensanierung</li>
              </ul>
            </div>

            <div className="service-detail-card">
              <h3>Hecken & Sträucher</h3>
              <ul>
                <li><CheckCircle size={18} /> Hecken schneiden</li>
                <li><CheckCircle size={18} /> Formschnitt</li>
                <li><CheckCircle size={18} /> Sträucher zurückschneiden</li>
                <li><CheckCircle size={18} /> Heckenrückschnitt</li>
              </ul>
            </div>

            <div className="service-detail-card">
              <h3>Baumschnitt</h3>
              <ul>
                <li><CheckCircle size={18} /> Baumschnitt (kleinere Bäume)</li>
                <li><CheckCircle size={18} /> Obstbaumschnitt</li>
                <li><CheckCircle size={18} /> Totholzentfernung</li>
                <li><CheckCircle size={18} /> Kronenpflege</li>
              </ul>
            </div>

            <div className="service-detail-card">
              <h3>Allgemeine Gartenpflege</h3>
              <ul>
                <li><CheckCircle size={18} /> Beetpflege</li>
                <li><CheckCircle size={18} /> Unkrautentfernung</li>
                <li><CheckCircle size={18} /> Pflanzarbeiten</li>
                <li><CheckCircle size={18} /> Mulchen</li>
              </ul>
            </div>

            <div className="service-detail-card">
              <h3>Saisonarbeiten</h3>
              <ul>
                <li><CheckCircle size={18} /> Laubarbeiten im Herbst</li>
                <li><CheckCircle size={18} /> Frühjahrsputz</li>
                <li><CheckCircle size={18} /> Wintervorbereitung</li>
                <li><CheckCircle size={18} /> Gartenpflege nach Urlaub</li>
              </ul>
            </div>

            <div className="service-detail-card">
              <h3>Entsorgung</h3>
              <ul>
                <li><CheckCircle size={18} /> Grünschnittentsorgung</li>
                <li><CheckCircle size={18} /> Laub entsorgen</li>
                <li><CheckCircle size={18} /> Astwerk abfahren</li>
                <li><CheckCircle size={18} /> Kompostierung</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-detail-cta">
        <div className="container">
          <h2>Gartenpflege anfragen</h2>
          <p>Ob einmalig oder regelmäßig – wir sind für Sie da. Rufen Sie uns an oder schreiben Sie uns.</p>
          <div className="cta-buttons">
            <a href="tel:+4917655669090" className="btn-cta">
              <Phone size={18} />
              0176 - 556 690 90
            </a>
            <Link to="/kontakt" className="btn-secondary">
              Kontaktformular
            </Link>
          </div>
        </div>
      </section>

      {/* Local SEO Links */}
      <section className="service-local-links">
        <div className="container">
          <h2>Gartenpflege in Ihrer Nähe</h2>
          <div className="local-links-grid">
            {[
              ['goettingen', 'Göttingen'], ['northeim', 'Northeim'], ['einbeck', 'Einbeck'],
              ['duderstadt', 'Duderstadt'], ['noerten-hardenberg', 'Nörten-Hardenberg'], ['bovenden', 'Bovenden'],
              ['hardegsen', 'Hardegsen'], ['osterode', 'Osterode'], ['herzberg', 'Herzberg'],
              ['bad-lauterberg', 'Bad Lauterberg'], ['moringen', 'Moringen'], ['uslar', 'Uslar'],
              ['hann-muenden', 'Hann. Münden'], ['gieboldehausen', 'Gieboldehausen'], ['dransfeld', 'Dransfeld'],
              ['hattorf', 'Hattorf'], ['dassel', 'Dassel'], ['katlenburg-lindau', 'Katlenburg-Lindau'],
            ].map(([slug, name]) => (
              <Link key={slug} to={`/gartenpflege/${slug}`} className="local-link-item">
                Gartenpflege {name}
              </Link>
            ))}
          </div>
          <div className="local-links-alt">
            <h3>Auch gefunden unter:</h3>
            <div className="local-links-grid">
              {[
                ['gartenservice/goettingen', 'Gartenservice Göttingen'],
                ['rasenmaehen/goettingen', 'Rasenmähen Göttingen'],
                ['heckenschnitt/goettingen', 'Heckenschnitt Göttingen'],
                ['gaertner/goettingen', 'Gärtner Göttingen'],
                ['gartenservice/northeim', 'Gartenservice Northeim'],
                ['rasenmaehen/northeim', 'Rasenmähen Northeim'],
                ['gartenservice/duderstadt', 'Gartenservice Duderstadt'],
                ['heckenschnitt/duderstadt', 'Heckenschnitt Duderstadt'],
              ].map(([path, label]) => (
                <Link key={path} to={`/${path}`} className="local-link-item alt">
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GartenpflegePage;
